import { useEffect, useMemo, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LabelHint from "@/components/ui/LabelHint";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  StockCount,
  StockCountLine,
} from "@/redux/services/inventory/inventory.type";
import {
  useDeleteStockCountMutation,
  useFinalizeStockCountMutation,
  useUpdateStockCountMutation,
} from "@/redux/services/inventory/stockCount.api";
import {
  displayPrice,
  formatStock,
  cn,
  normalizeDecimalInput,
  stripTrailingZeros,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertTriangle,
  MoreVertical,
  Search,
  Trash2,
} from "lucide-react";

const PACKAGING_BASE_SENTINEL = "__base__";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { usePermissions } from "@/hooks/usePermissions";
import toast from "react-hot-toast";
import {
  STOCK_COUNT_STATUS_LABELS,
  STOCK_COUNT_STATUS_STYLES,
} from "./stockCountStatus";

interface StockCountEntryTableProps {
  stockCount: StockCount;
}

const varianceFor = (expected: string, counted: string | null) => {
  if (counted === null || counted === "") return null;
  const e = parseFloat(expected) || 0;
  const c = parseFloat(counted) || 0;
  return (c - e).toFixed(2);
};

const initialCountedFor = (line: StockCountLine) => {
  if (line.countedPackaging && line.countedPackageQuantity !== null) {
    return stripTrailingZeros(line.countedPackageQuantity);
  }
  return stripTrailingZeros(line.countedQuantity ?? "");
};

const initialPackagingFor = (line: StockCountLine) =>
  line.countedPackaging?.id ?? "";

const baseEquivalent = (raw: string, line: StockCountLine, packagingId: string) => {
  if (raw === "") return null;
  const qty = parseFloat(normalizeDecimalInput(raw) ?? "");
  if (Number.isNaN(qty)) return null;
  if (!packagingId) return qty.toString();
  const pack = line.ingredient.packagings?.find((p) => p.id === packagingId);
  if (!pack) return qty.toString();
  const factor = parseFloat(pack.quantityInBase) || 0;
  return (qty * factor).toString();
};

const StockCountEntryTable = ({ stockCount }: StockCountEntryTableProps) => {
  const isLocked = stockCount.status !== "in_progress";
  const [draftValues, setDraftValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      stockCount.lines.map((line) => [line.id, initialCountedFor(line)])
    )
  );
  const [draftPackaging, setDraftPackaging] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        stockCount.lines.map((line) => [line.id, initialPackagingFor(line)])
      )
  );
  const [draftNotes, setDraftNotes] = useState(stockCount.notes ?? "");
  const [search, setSearch] = useState("");
  const [lineFilter, setLineFilter] = useState<
    "all" | "uncounted" | "variances"
  >("all");

  useEffect(() => {
    setDraftValues(
      Object.fromEntries(
        stockCount.lines.map((line) => [line.id, initialCountedFor(line)])
      )
    );
    setDraftPackaging(
      Object.fromEntries(
        stockCount.lines.map((line) => [line.id, initialPackagingFor(line)])
      )
    );
    setDraftNotes(stockCount.notes ?? "");
  }, [stockCount]);

  const [updateStockCount, { isLoading: isSaving }] =
    useUpdateStockCountMutation();
  const [finalizeStockCount, { isLoading: isFinalizing }] =
    useFinalizeStockCountMutation();
  const [deleteStockCount] = useDeleteStockCountMutation();
  const navigate = useNavigate();
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const canManageInventory = canManage("inventory");

  const hasChanges = useMemo(() => {
    if (draftNotes !== (stockCount.notes ?? "")) return true;
    return stockCount.lines.some(
      (line) =>
        draftValues[line.id] !== initialCountedFor(line) ||
        draftPackaging[line.id] !== initialPackagingFor(line)
    );
  }, [draftValues, draftPackaging, draftNotes, stockCount]);

  // Progress: how many lines have a counted value (draft or saved)
  const totalLines = stockCount.lines.length;
  const countedLines = stockCount.lines.filter((line) => {
    const v = draftValues[line.id];
    return v !== undefined && v !== "";
  }).length;
  const progressPct =
    totalLines > 0 ? Math.min(100, (countedLines / totalLines) * 100) : 0;

  // Variance summary (live, based on drafts)
  const varianceSummary = useMemo(() => {
    let variancesCount = 0;
    let costImpactCents = 0;
    stockCount.lines.forEach((line) => {
      const counted = draftValues[line.id] ?? "";
      const baseCounted = baseEquivalent(
        counted,
        line,
        draftPackaging[line.id] ?? ""
      );
      const variance = varianceFor(line.expectedQuantity, baseCounted);
      if (variance === null) return;
      const n = parseFloat(variance);
      if (n === 0) return;
      variancesCount += 1;
      costImpactCents += Math.abs(n) * line.ingredient.costPerUnit;
    });
    return {
      variancesCount,
      costImpactCents: Math.round(costImpactCents),
    };
  }, [stockCount.lines, draftValues, draftPackaging]);

  // Filtered lines view
  const filteredLines = useMemo(() => {
    const q = search.toLowerCase().trim();
    return stockCount.lines.filter((line) => {
      if (q && !line.ingredient.name.toLowerCase().includes(q)) return false;
      const v = draftValues[line.id];
      if (lineFilter === "uncounted") {
        if (v !== undefined && v !== "") return false;
      } else if (lineFilter === "variances") {
        const baseCounted = baseEquivalent(
          v ?? "",
          line,
          draftPackaging[line.id] ?? ""
        );
        const variance = varianceFor(line.expectedQuantity, baseCounted);
        if (variance === null || parseFloat(variance) === 0) return false;
      }
      return true;
    });
  }, [stockCount.lines, search, lineFilter, draftValues, draftPackaging]);

  // Unsaved-changes guard — browser reload/close
  useEffect(() => {
    if (!hasChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  // Unsaved-changes guard — in-app navigation
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    confirm({
      title: "Modifications non enregistrées",
      description:
        "Vos saisies seront perdues si vous quittez. Voulez-vous continuer ?",
      confirmText: "Quitter",
      cancelText: "Rester",
      variant: "destructive",
      icon: <AlertTriangle className="h-6 w-6" />,
    }).then((ok) => {
      if (ok) {
        blocker.proceed?.();
      } else {
        blocker.reset?.();
      }
    });
  }, [blocker, confirm]);

  const handleSave = async () => {
    const countsPayload = stockCount.lines
      .filter(
        (line) =>
          draftValues[line.id] !== initialCountedFor(line) ||
          draftPackaging[line.id] !== initialPackagingFor(line)
      )
      .map((line) => {
        // "1,5" (French keypad) goes out as "1.5"; a malformed value is sent
        // as typed so the API's 422 names the line.
        const typed = draftValues[line.id];
        const raw =
          typed === undefined ? undefined : normalizeDecimalInput(typed) ?? typed;
        const packagingId = draftPackaging[line.id] ?? "";
        if (raw === "" || raw === undefined) {
          return {
            lineUuid: line.id,
            countedQuantity: null,
            countedPackaging: null,
            countedPackageQuantity: null,
          };
        }
        if (packagingId) {
          return {
            lineUuid: line.id,
            countedPackaging: `/api/ingredient_packagings/${packagingId}`,
            countedPackageQuantity: raw,
          };
        }
        return {
          lineUuid: line.id,
          countedQuantity: raw,
          countedPackaging: null,
          countedPackageQuantity: null,
        };
      });

    const payload: { notes?: string; counts?: typeof countsPayload } = {};
    if (draftNotes !== (stockCount.notes ?? "")) payload.notes = draftNotes;
    if (countsPayload.length > 0) payload.counts = countsPayload;

    if (!payload.notes && !payload.counts) return;

    const res = await updateStockCount({ uuid: stockCount.id, data: payload });
    if ("data" in res) {
      toast.success("Quantités enregistrées");
    } else {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleFinalize = async () => {
    if (hasChanges) {
      toast.error("Enregistrez d'abord vos modifications");
      return;
    }
    const ok = await confirm({
      title: "Finaliser cet inventaire ?",
      description:
        "Cela créera des ajustements de type correction pour toutes les variances non nulles. Action irréversible.",
      confirmText: "Finaliser",
      cancelText: "Retour",
      variant: "destructive",
      icon: <AlertTriangle className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await finalizeStockCount({ uuid: stockCount.id });
    if ("data" in res) {
      toast.success("Inventaire finalisé");
    } else {
      toast.error("Erreur lors de la finalisation");
    }
  };

  const handleDeleteCount = async () => {
    const ok = await confirm({
      title: "Supprimer cet inventaire ?",
      description:
        "Toutes les saisies seront définitivement perdues. Action irréversible.",
      confirmText: "Supprimer",
      cancelText: "Retour",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    // Reset draft state so the navigation guard doesn't trigger
    setDraftValues(
      Object.fromEntries(
        stockCount.lines.map((line) => [line.id, initialCountedFor(line)])
      )
    );
    setDraftPackaging(
      Object.fromEntries(
        stockCount.lines.map((line) => [line.id, initialPackagingFor(line)])
      )
    );
    setDraftNotes(stockCount.notes ?? "");
    const res = await deleteStockCount({ uuid: stockCount.id });
    if ("error" in res) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Inventaire supprimé");
    navigate("/inventory/stock-counts");
  };

  return (
    <div className="space-y-4">
      <div className="dash__card space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-card-foreground">
                Inventaire du{" "}
                {format(parseISO(stockCount.countedAt), "d MMMM yyyy", {
                  locale: fr,
                })}
              </h2>
              <span
                className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                  STOCK_COUNT_STATUS_STYLES[stockCount.status]
                )}
              >
                {STOCK_COUNT_STATUS_LABELS[stockCount.status]}
              </span>
              {hasChanges && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Modifications non enregistrées
                </span>
              )}
            </div>
            {stockCount.finalizedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Finalisé le{" "}
                {format(parseISO(stockCount.finalizedAt), "d MMM yyyy, HH:mm", {
                  locale: fr,
                })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {canManageInventory && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSave}
                isLoading={isSaving}
                disabled={isLocked || !hasChanges}
              >
                Enregistrer
              </Button>
            )}
            {canManageInventory && (
              <Button
                type="button"
                onClick={handleFinalize}
                isLoading={isFinalizing}
                disabled={isLocked}
              >
                Finaliser
              </Button>
            )}
            {!isLocked && canManageInventory && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="h-10 w-10 rounded-md border border-border bg-white hover:bg-muted flex items-center justify-center text-muted-foreground"
                    aria-label="Plus d'actions"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={handleDeleteCount}
                    className="text-danger focus:text-danger justify-start"
                  >
                    Supprimer l'inventaire
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {!isLocked && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progression</span>
              <span className="font-medium">
                {countedLines} / {totalLines} comptés · {progressPct.toFixed(0)}%
              </span>
            </div>
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all",
                  progressPct >= 100 ? "bg-green-500" : "bg-blue-500"
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        <div>
          <label
            htmlFor="stock-count-notes"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Notes
          </label>
          <textarea
            id="stock-count-notes"
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={isLocked}
            rows={2}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:bg-muted disabled:text-muted-foreground"
            placeholder="Notes internes..."
          />
        </div>
      </div>

      <div className="dash__card space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un ingrédient..."
              className="w-full pl-9 pr-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="inline-flex items-center rounded-md border border-border bg-muted/40 p-1 text-sm self-start sm:self-auto">
            {(
              [
                { id: "all", label: "Tous" },
                { id: "uncounted", label: "Non comptés" },
                { id: "variances", label: "Avec écarts" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setLineFilter(opt.id)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                  lineFilter === opt.id
                    ? "bg-card text-card-foreground shadow-sm"
                    : "text-muted-foreground hover:text-card-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Ingrédient
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Attendu
                  <LabelHint text="Quantité attendue selon les mouvements de stock, en unité de base." />
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground w-40">
                  Compté
                  <LabelHint text="Quantité comptée. Si l'ingrédient a des conditionnements, vous pouvez choisir celui dans lequel vous comptez (sac, carton…) — la conversion est automatique." />
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Écart
                  <LabelHint text="Différence entre compté et attendu, en unité de base." />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLines.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    {search || lineFilter !== "all"
                      ? "Aucun ingrédient ne correspond"
                      : "Aucune ligne"}
                  </td>
                </tr>
              ) : (
                filteredLines.map((line) => {
                  const countedValue = draftValues[line.id] ?? "";
                  const packagingId = draftPackaging[line.id] ?? "";
                  const packagings = line.ingredient.packagings ?? [];
                  const selectedPackaging = packagings.find(
                    (p) => p.id === packagingId
                  );
                  const baseCounted = baseEquivalent(
                    countedValue,
                    line,
                    packagingId
                  );
                  const variance = varianceFor(
                    line.expectedQuantity,
                    baseCounted
                  );
                  const varianceNum = variance ? parseFloat(variance) : 0;
                  const isRowDirty =
                    !isLocked &&
                    (countedValue !== initialCountedFor(line) ||
                      packagingId !== initialPackagingFor(line));
                  return (
                    <tr key={line.id} className="border-b border-border">
                      <td className="py-2 px-3 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <span>{line.ingredient.name}</span>
                          {isRowDirty && (
                            <span
                              title="Modification non enregistrée"
                              className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0"
                            />
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground">
                        {formatStock(
                          line.expectedQuantity,
                          line.ingredient.baseUnit
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={countedValue}
                          onChange={(e) =>
                            setDraftValues((prev) => ({
                              ...prev,
                              [line.id]: e.target.value,
                            }))
                          }
                          disabled={isLocked}
                          className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-muted disabled:text-muted-foreground"
                          placeholder={stripTrailingZeros(line.expectedQuantity)}
                        />
                        {packagings.length > 0 && (
                          <Select
                            value={packagingId || PACKAGING_BASE_SENTINEL}
                            onValueChange={(v) =>
                              setDraftPackaging((prev) => ({
                                ...prev,
                                [line.id]:
                                  v === PACKAGING_BASE_SENTINEL ? "" : v,
                              }))
                            }
                            disabled={isLocked}
                          >
                            <SelectTrigger className="mt-1 h-9 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={PACKAGING_BASE_SENTINEL}>
                                {line.ingredient.baseUnit}
                              </SelectItem>
                              {packagings.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} (×
                                  {parseFloat(p.quantityInBase)}{" "}
                                  {line.ingredient.baseUnit})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {selectedPackaging &&
                          baseCounted !== null &&
                          countedValue !== "" && (
                            <p className="mt-1 text-[11px] text-muted-foreground">
                              = {baseCounted} {line.ingredient.baseUnit}
                            </p>
                          )}
                      </td>
                      <td className="py-2 px-3 text-sm">
                        {variance === null ? (
                          <span className="text-muted-foreground/60">—</span>
                        ) : (
                          <span
                            className={cn(
                              "font-medium",
                              varianceNum < 0
                                ? "text-red-600"
                                : varianceNum > 0
                                ? "text-green-600"
                                : "text-muted-foreground"
                            )}
                          >
                            {varianceNum > 0 ? "+" : ""}
                            {formatStock(variance, line.ingredient.baseUnit)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {varianceSummary.variancesCount > 0 && (
          <div className="pt-3 mt-3 border-t border-border flex items-center justify-between gap-3 flex-wrap text-sm">
            <span className="text-card-foreground">
              <strong>{varianceSummary.variancesCount}</strong> ligne
              {varianceSummary.variancesCount > 1 ? "s" : ""} avec écart
            </span>
            {varianceSummary.costImpactCents > 0 && (
              <span className="text-muted-foreground">
                Impact coût estimé :{" "}
                <strong className="text-card-foreground">
                  {displayPrice(varianceSummary.costImpactCents, currency)}
                </strong>
              </span>
            )}
          </div>
        )}

        {isLocked && (
          <p className="text-xs text-muted-foreground mt-3">
            Cet inventaire est finalisé et ne peut plus être modifié.
          </p>
        )}
      </div>

    </div>
  );
};

export default StockCountEntryTable;

export const StockCountEntryTableSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="w-full h-20" />
    <Skeleton className="w-full h-40" />
  </div>
);
